import { BaseAPI, Configuration, type ConfigurationParameters } from '@ps/api';
import { config } from '@ps/config/client';
import 'client-only';

import { getDeviceId } from './deviceId';

type ApiConfigParams = Omit<ConfigurationParameters, 'basePath'>;

type ApiClass<T extends BaseAPI> = new (configuration: Configuration) => T;

/**
 * Create an API Configuration with default basePath set to config.apiEndpoint
 *
 * @param params - Optional configuration parameters (accessToken, headers, etc.)
 * @returns Configuration instance with default basePath
 *
 * @example
 * ```ts
 * // Create config with default settings
 * const apiConfig = createApiConfig();
 *
 * // Create config with access token
 * const apiConfig = createApiConfig({ accessToken: idToken });
 *
 * // Create config with custom headers
 * const apiConfig = createApiConfig({
 *   accessToken: idToken,
 *   headers: { 'X-Custom-Header': 'value' },
 * });
 * ```
 */
export function createApiConfig(params?: ApiConfigParams): Configuration {
  return new Configuration({
    basePath: config.apiEndpoint,
    headers: {
      'X-Pop-Platform': 'popstore',
      'X-Device-ID': getDeviceId(),
    },
    ...params,
  });
}

/**
 * Create an API instance with default configuration
 *
 * @param Api - The API class to instantiate (e.g., OAuth2Api, UserApi)
 * @param params - Optional configuration parameters (accessToken, headers, etc.)
 * @returns Instance of the API class with default basePath
 *
 * @example
 * ```ts
 * // Create API with default settings
 * const api = withAPI(OAuth2Api);
 *
 * // Create API with access token
 * const api = withAPI(OAuth2Api, { accessToken: idToken });
 *
 * // Create API with custom headers
 * const api = withAPI(UserApi, {
 *   accessToken: idToken,
 *   headers: { 'X-Custom-Header': 'value' },
 * });
 * ```
 */
export function withAPI<T extends BaseAPI>(
  Api: ApiClass<T>,
  params?: ApiConfigParams,
): T {
  return new Api(createApiConfig(params));
}

type TokenResult = string | null | undefined;

type ClientToken =
  | TokenResult
  | Promise<TokenResult>
  | (() => TokenResult | Promise<TokenResult>);

type ClientApiParams = Omit<ApiConfigParams, 'accessToken'> & {
  token?: ClientToken;
};

/**
 * Create an API instance that resolves an access token automatically.
 *
 * `token` accepts a string, a Promise, or a factory function.
 * When the resolved value is `null`/`undefined`, Firebase auth is used
 * as a fallback.  When omitted entirely, Firebase auth is always used.
 *
 * @example
 * ```ts
 * // Firebase auth (default)
 * const api = withClientAPI(OrderApi);
 *
 * // Explicit token (iframe / URL param)
 * const api = withClientAPI(OrderApi, { token: iframeToken });
 *
 * // Async token that falls back to Firebase when null
 * const api = withClientAPI(OrderApi, { token: getAuthToken() });
 *
 * // Factory — called lazily on each request
 * const api = withClientAPI(OrderApi, { token: () => getAuthToken() });
 * ```
 */
export function withClientAPI<T extends BaseAPI>(
  Api: ApiClass<T>,
  params?: ClientApiParams,
): T {
  const { token, ...rest } = params ?? {};

  const firebaseToken = async () => {
    // Lazy-import the firebase SDK so it loads on the FIRST API request
    // (post-hydration), not in every client page's first-load bundle. The token
    // resolver is already invoked per-request, so this only defers WHEN firebase
    // loads — the visitor bio's first paint no longer ships firebase/auth.
    const { auth } = await import('../firebase/auth');
    const authInstance = auth();
    await authInstance.authStateReady();
    return (await authInstance.currentUser?.getIdToken()) ?? '';
  };

  let resolved: ApiConfigParams['accessToken'];
  if (typeof token === 'string') {
    resolved = token;
  } else if (typeof token === 'function') {
    // Factory — call lazily per request, fall back to Firebase if null
    resolved = async () => (await token()) ?? (await firebaseToken());
  } else if (token != null) {
    // Promise — await it, fall back to Firebase if null
    resolved = async () => (await token) ?? (await firebaseToken());
  } else {
    resolved = firebaseToken;
  }

  return withAPI(Api, { ...rest, accessToken: resolved });
}
